// Detect regulated PII sitting in a free-text field (note bodies, and any
// other plaintext column a person can type into).
//
// WHY THIS EXISTS (Joy, 2026-08-03). Joy needed to record a client's payment
// details and the OS gave her nowhere to put them — the contact record could
// DISPLAY a bank account that came over from ActiveCampaign but had no way to
// enter one. So she typed the routing and account number into a note. That is
// exactly the right instinct and exactly the wrong column: every protection the
// OS builds for this data (AES-GCM at rest, the role matrix, the audit row on
// every reveal, the rate limit, the safe projection that keeps `*_encrypted`
// out of every response) is bypassed the moment the value lands in `notes.body`
// as readable text. Notes also feed AI summaries and CSV exports.
//
// The fix is two-sided: give her a real field (the bank write path), and notice
// when regulated PII goes somewhere it shouldn't. This module is the noticing
// half, shared by both consumers so they can never disagree about what counts:
//
//   * the note composer  — warns as you type, and names the right destination
//   * the note-pii sweep — finds what is already stored (migration pass)
//
// DESIGN RULE — checksums over keyword guessing. A naive "9+ digits in a row"
// scan fires on policy numbers, loan numbers, claim numbers, and phone strings,
// and advisors quickly learn to ignore a warning that cries wolf. So the two
// highest-consequence shapes are identified by their actual check digits:
// ABA routing numbers carry a weighted mod-10 checksum, and payment cards carry
// Luhn. A random 9-digit policy number passes the ABA check about 10% of the
// time — and we require a nearby banking word for the weaker signals — which
// keeps false positives low enough that the warning stays worth reading.
//
// PII RULE — this module NEVER returns, logs, or stores the matched value.
// A finding carries its kind, its offset, and its length. Callers that already
// hold the text (the composer, in the user's own browser) can highlight it;
// callers that must not see it (the sweep's report, audit rows) get a shape
// description and nothing more. Follows the name-repair precedent, where the
// full corrupted value never reached a log, plan, audit row, or report.

/** What kind of regulated value the text appears to contain. */
export type PiiKind = "ssn" | "bank_routing_number" | "bank_account_number" | "payment_card";

export interface PiiFinding {
  kind: PiiKind;
  /** Index of the first character of the match within the scanned text. */
  index: number;
  /** Length of the match, in characters. */
  length: number;
  /** How many digits the match contains (shape only — never the value). */
  digits: number;
}

/** Plain-English label + where the value actually belongs. Used verbatim by
 *  the composer warning, so the person who typed it knows what to do next
 *  rather than just being told "no". */
export const PII_KIND_COPY: Record<PiiKind, { label: string; destination: string }> = {
  ssn: {
    label: "a Social Security number",
    destination: "the SSN field on the contact record (Identification card)",
  },
  bank_routing_number: {
    label: "a bank routing number",
    destination: "Bank details on the contact record (Identification card)",
  },
  bank_account_number: {
    label: "a bank account number",
    destination: "Bank details on the contact record (Identification card)",
  },
  payment_card: {
    label: "a payment card number",
    destination: "nowhere in Stewardship OS — card numbers are never stored here",
  },
};

// Words that make a nearby digit run much more likely to be banking data.
// Matched case-insensitively against a window of text around the digits.
const BANK_WORDS =
  /\b(bank|banking|routing|route|rtg|rtn|aba|acct|account|checking|chequing|savings|ach|autopay|auto-?pay|draft|direct\s*deposit|wire)\b/i;
const SSN_WORDS = /\b(ssn|social\s*security|soc\s*sec|tin|itin)\b/i;
const CARD_WORDS =
  /\b(card|visa|mastercard|amex|american\s*express|discover|debit|credit|cvv|cvc|exp(?:iry|iration)?)\b/i;

/** How much surrounding text counts as "nearby" for a keyword. Generous
 *  enough to catch "Routing:\n123456789" and a label on the previous line. */
const CONTEXT_RADIUS = 40;

function contextAround(text: string, index: number, length: number): string {
  return text.slice(
    Math.max(0, index - CONTEXT_RADIUS),
    Math.min(text.length, index + length + CONTEXT_RADIUS),
  );
}

/**
 * ABA routing-number check digit (mod 10, weights 3-7-1 repeating).
 * Every real U.S. routing number satisfies this; it is the single most
 * reliable way to tell one from an arbitrary 9-digit reference number.
 */
export function isValidAbaRouting(digits: string): boolean {
  if (!/^\d{9}$/.test(digits)) return false;
  const w = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(digits[i]) * (w[i] as number);
  return sum % 10 === 0 && sum > 0;
}

/** Luhn check — payment cards (and nothing else we store) satisfy it. */
export function passesLuhn(digits: string): boolean {
  if (!/^\d+$/.test(digits)) return false;
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = Number(digits[i]);
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return sum % 10 === 0;
}

// A run of digits, optionally broken by single spaces or dashes the way
// people actually type account numbers ("1234 5678 9012"). Captured whole so
// the finding's offset/length cover what the person would recognise.
const DIGIT_RUN = /\d(?:[\d \-]{2,24}\d)?/g;
// One unbroken group of digits inside such a run.
const DIGIT_GROUP = /\d+/g;

function digitsOf(s: string): string {
  return s.replace(/\D/g, "");
}

interface Candidate {
  index: number;
  length: number;
  digits: string;
}

/**
 * Every span of the text worth classifying.
 *
 * Two levels, because both spellings are real and neither alone is enough:
 *
 *   * the whole separated run — "1234 5678 9012" is ONE account number, and
 *     scanning only unbroken groups would see three harmless short numbers;
 *   * each unbroken group inside it — "123456789 1234567890" (a routing
 *     number and an account number typed side by side, which is exactly the
 *     shape of the note that started this) is TWO values, and scanning only
 *     whole runs would see one 19-digit blob matching nothing.
 *
 * Both are emitted; `findPiiInText` de-overlaps afterwards, preferring the
 * strongest classification. Missing a real one costs far more than
 * evaluating a span twice.
 */
function candidateSpans(text: string): Candidate[] {
  const out: Candidate[] = [];
  for (const m of text.matchAll(DIGIT_RUN)) {
    const raw = m[0];
    const index = m.index ?? 0;
    out.push({ index, length: raw.length, digits: digitsOf(raw) });
    // Only worth splitting when the run actually contains a separator.
    if (!/[ \-]/.test(raw)) continue;
    for (const g of raw.matchAll(DIGIT_GROUP)) {
      const gi = index + (g.index ?? 0);
      out.push({ index: gi, length: g[0].length, digits: g[0] });
    }
  }
  // Longest-first so a whole run is considered before its own fragments.
  return out.sort((a, b) => b.length - a.length || a.index - b.index);
}

/**
 * Scan free text for regulated PII.
 *
 * Conservative by construction: a bare digit run with no checksum hit and no
 * banking/SSN word nearby produces NOTHING, because policy numbers, loan
 * numbers, and claim numbers look exactly like that and are perfectly fine to
 * write in a note.
 *
 * Returns findings sorted by position, de-overlapped (the strongest kind wins
 * for any given run), and never carrying the matched value.
 */
export function findPiiInText(text: string): PiiFinding[] {
  if (!text) return [];
  const findings: PiiFinding[] = [];

  // Explicit dashed SSN — unambiguous on its own, no keyword required.
  const dashedSsn = /\b\d{3}-\d{2}-\d{4}\b/g;
  for (const m of text.matchAll(dashedSsn)) {
    findings.push({
      kind: "ssn",
      index: m.index ?? 0,
      length: m[0].length,
      digits: 9,
    });
  }

  for (const cand of candidateSpans(text)) {
    const { index, length, digits } = cand;
    // Skip anything already CLAIMED by a stronger/longer match. Spans that
    // were merely considered and matched nothing never claim anything, so
    // their fragments still get their own turn.
    if (findings.some((f) => index < f.index + f.length && f.index < index + length)) {
      continue;
    }
    const n = digits.length;
    if (n < 6) continue; // too short to be any of these
    const ctx = contextAround(text, index, length);

    // Payment card — Luhn plus a plausible card length. Cards are never
    // stored in Stewardship OS at all (AC's card/CVV fields were purged at
    // source before export), so this is always worth surfacing.
    if (n >= 13 && n <= 19 && passesLuhn(digits) && (CARD_WORDS.test(ctx) || n === 16)) {
      findings.push({ kind: "payment_card", index, length, digits: n });
      continue;
    }

    // Routing number — the ABA checksum is the strong signal. A keyword
    // alone also qualifies, since a mistyped routing number is still a
    // routing number sitting in a note.
    if (n === 9 && (isValidAbaRouting(digits) || BANK_WORDS.test(ctx))) {
      findings.push({
        kind: "bank_routing_number",
        index,
        length,
        digits: n,
      });
      continue;
    }

    // SSN — 9 digits with an SSN word nearby (the dashed form was already
    // caught above without needing one).
    if (n === 9 && SSN_WORDS.test(ctx)) {
      findings.push({ kind: "ssn", index, length, digits: n });
      continue;
    }

    // Bank account number — no checksum exists, so this one REQUIRES a
    // banking word nearby. Real account numbers run about 6-17 digits.
    if (n >= 6 && n <= 17 && BANK_WORDS.test(ctx)) {
      findings.push({
        kind: "bank_account_number",
        index,
        length,
        digits: n,
      });
    }
  }

  // Second pass — a confirmed value is itself banking context.
  //
  // Found by running the real sweep (2026-08-03): the note that started all
  // of this reads "<routing> <account>" with no labels at all. The routing
  // number is caught by its check digit, but the account number beside it
  // matched nothing — no checksum exists for it and there is no banking WORD
  // anywhere in the note, only another bank number. Redaction would then have
  // stripped the routing number and left the account number sitting in plain
  // text, which is worse than doing nothing: the note looks cleaned.
  //
  // So: once a routing number or card number is confirmed, treat it the way a
  // banking keyword would be treated, and re-examine unclaimed digit runs near
  // it. Deliberately narrow — it needs an already-confirmed value nearby, so a
  // note full of ordinary reference numbers still produces nothing.
  const anchors = findings.filter(
    (f) => f.kind === "bank_routing_number" || f.kind === "payment_card",
  );
  if (anchors.length > 0) {
    for (const cand of candidateSpans(text)) {
      const { index, length, digits } = cand;
      const n = digits.length;
      if (n < 6 || n > 17) continue;
      if (findings.some((f) => index < f.index + f.length && f.index < index + length)) {
        continue;
      }
      const nearAnchor = anchors.some(
        (a) =>
          index < a.index + a.length + CONTEXT_RADIUS && a.index < index + length + CONTEXT_RADIUS,
      );
      if (!nearAnchor) continue;
      findings.push({
        kind: "bank_account_number",
        index,
        length,
        digits: n,
      });
    }
  }

  return findings.sort((a, b) => a.index - b.index);
}

/** True when the text appears to contain any regulated PII. */
export function containsPii(text: string): boolean {
  return findPiiInText(text).length > 0;
}

/** The distinct kinds present, in a stable order — what the composer warning
 *  and the sweep report both summarise on. */
export function piiKindsInText(text: string): PiiKind[] {
  const order: PiiKind[] = ["payment_card", "ssn", "bank_routing_number", "bank_account_number"];
  const present = new Set(findPiiInText(text).map((f) => f.kind));
  return order.filter((k) => present.has(k));
}

/**
 * Replace every finding with a marker, returning text safe to store.
 *
 * Used by the note-pii sweep. The removed value is NOT retained anywhere —
 * not in the returned string, not in a version snapshot, not in an audit row.
 * That makes redaction ONE-WAY on purpose (the name-repair precedent for
 * driver's-license numbers): the alternative is copying regulated PII into a
 * second table so it can be un-redacted, which defeats the entire point.
 */
export function redactPii(text: string): { redacted: string; removed: PiiKind[] } {
  const findings = findPiiInText(text);
  if (findings.length === 0) return { redacted: text, removed: [] };
  let out = "";
  let cursor = 0;
  for (const f of findings) {
    out += text.slice(cursor, f.index);
    out += `[${PII_KIND_COPY[f.kind].label} removed — see the contact's Bank details]`;
    cursor = f.index + f.length;
  }
  out += text.slice(cursor);
  return { redacted: out, removed: piiKindsInText(text) };
}
